// The value an action button asks for BEFORE it runs (ActionDef.capture): a
// code scanned with the camera / an external scanner, or a typed one. Every
// action runner (the open-record bar, a line's editor, the list's row menu)
// wires it the same way: `captureFor(def)` resolves with the value, or null
// when the user cancelled — then nothing runs.
//
// Scanning is the shared ScanCapture (its entry box doubles as the
// keyboard-wedge path for an external scanner). Typing is the small box
// below. Portal + z-index mirror ActionConfirmModal: this opens from inside
// panels that form their own stacking contexts.

import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { ScanCapture } from '../scan/ScanCapture'
import { tw } from '../testHooks'
import { actionCaptureOf, type ActionCapture, type ActionDef } from './types'

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 12000,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: '0.9225rem', borderRadius: 6,
  border: '1px solid var(--color-border, #ccc)', background: 'var(--color-bg, #fff)',
  color: 'var(--color-text, #111)', boxSizing: 'border-box',
}

function TypedValueModal({ title, prompt, onSubmit, onCancel }: {
  title: string
  prompt: string
  onSubmit: (value: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState('')
  const canSubmit = value.trim() !== ''
  const submit = () => { if (canSubmit) onSubmit(value.trim()) }
  if (typeof document === 'undefined') return null // SSR safety
  return createPortal(
    <div style={overlayStyle} onClick={onCancel}>
      <div
        onClick={e => e.stopPropagation()}
        data-tw-dialog="action_capture"
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(420px, 94vw)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9625rem' }}>{title}</span>
          <button
            onClick={onCancel}
            {...tw('action', 'close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1625rem', color: 'var(--color-text-secondary, #666)' }}
            title="Cancel">×</button>
        </div>
        <label style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary, #666)' }}>{prompt}</span>
          <input
            autoFocus style={inputStyle} value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }} />
        </label>
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            onClick={onCancel}
            {...tw('action', 'cancel')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text, #111)' }}>
            Cancel
          </button>
          <button
            onClick={submit} disabled={!canSubmit}
            {...tw('action', 'confirm')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: canSubmit ? 'var(--color-accent, #4c8dff)' : 'var(--color-border, #ccc)', color: '#fff', cursor: canSubmit ? 'pointer' : 'default' }}>
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface Pending {
  name: string
  capture: ActionCapture
  resolve: (value: string | null) => void
}

/**
 * `captureFor(def)` resolves with the value the action asks for — immediately
 * with '' when it asks for none — or null when the user cancelled. Render
 * `actionCaptureModal` somewhere in the host tree.
 */
export function useActionCapture(): {
  captureFor: (def: ActionDef | undefined) => Promise<string | null>
  actionCaptureModal: React.ReactNode
} {
  const [pending, setPending] = useState<Pending | null>(null)

  const captureFor = useCallback((def: ActionDef | undefined) => {
    const capture = actionCaptureOf(def)
    if (!def || !capture) return Promise.resolve('')
    return new Promise<string | null>((resolve) => {
      setPending({ name: def.name, capture, resolve })
    })
  }, [])

  const finish = (value: string | null) => {
    setPending((p) => { p?.resolve(value); return null })
  }

  let actionCaptureModal: React.ReactNode = null
  if (pending) {
    const prompt = (pending.capture.prompt || '').trim()
    actionCaptureModal = pending.capture.mode === 'scan'
      ? <ScanCapture
          title={prompt || pending.name}
          onScan={(code) => finish(code.trim() || null)}
          onClose={() => finish(null)} />
      : <TypedValueModal
          title={pending.name}
          prompt={prompt || 'Enter the code'}
          onSubmit={finish}
          onCancel={() => finish(null)} />
  }
  return { captureFor, actionCaptureModal }
}
